import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, ExternalLink, Image as ImageIcon, Loader2, Lock, Sparkles, TriangleAlert } from "lucide-react";
import type { BroadcastLinkData } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";
import { useTenant } from "@/lib/hooks/useTenant";
import { useEntitlements } from "@/lib/entitlements";
import { useLinks } from "@/lib/hooks/useLinks";
import { useCreateScheduledPost } from "@/lib/hooks/useScheduledPosts";
import { useSaveRecurringPost } from "@/lib/hooks/useRecurringPosts";
import { useGenerateCopy } from "@/lib/hooks/useAiCopy";
import { defaultDraft, draftEnd, draftStart, draftToRule, type RecurrenceDraft } from "@/lib/recurrence-draft";
import type { Link as AffiliateLink } from "@/lib/types";
import { BroadcastTextPicker } from "@/components/posts/BroadcastTextPicker";
import { DestinationPicker, toDeliveries, type DestinationOption } from "@/components/posts/DestinationPicker";
import { PostPreviewSimulator } from "@/components/posts/PostPreviewSimulator";
import { PublishModeCard, type PublishMode } from "@/components/posts/PublishModeCard";

const MAX_CHARACTERS = 2200;

type Feedback = { kind: "success" | "error"; message: string } | null;

/** Data e hora digitadas no horario de Brasilia -> ISO UTC. */
export function scheduleToIso(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const value = new Date(`${date}T${time}:00-03:00`);
  return Number.isNaN(value.getTime()) ? null : value.toISOString();
}

/** Dados do link para as variaveis dos textos de disparo. */
export function linkData(link: AffiliateLink | undefined): BroadcastLinkData | null {
  if (!link) return null;
  return {
    title: link.title ?? null,
    priceCents: link.price_cents ?? null,
    originalPriceCents: link.original_price_cents ?? null,
    coupon: link.coupon ?? null,
    url: link.destination_url,
    marketplaceCode: link.marketplace_code,
  };
}

function successMessage(mode: PublishMode): string {
  if (mode === "recurring") return "Publicação recorrente salva. Acompanhe em Publicações recorrentes.";
  if (mode === "schedule") return "Post agendado com sucesso. Acompanhe o envio no Calendário.";
  return "Post na fila de envio - deve sair em até 30 segundos.";
}

function ProBanner(): JSX.Element {
  return (
    <Card variant="ghost" className="border-[hsl(var(--gold)/0.35)]">
      <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--gold)/0.12)] text-[hsl(var(--gold))]">
            <Lock size={16} />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">Disparos, agendamento e recorrência são PRO</p>
            <p className="text-xs text-muted-foreground">Disponível nos planos Pro e Pro+IA.</p>
          </div>
        </div>
        <Button asChild variant="gold" size="sm">
          <Link to="/app/plans">
            <Sparkles size={14} />
            Ver planos
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function FeedbackBanner({ feedback }: { feedback: NonNullable<Feedback> }): JSX.Element {
  return (
    <div
      role={feedback.kind === "error" ? "alert" : "status"}
      className={cn(
        "flex items-center gap-3 rounded-lg border px-4 py-3 text-sm",
        feedback.kind === "success" ? "border-primary/25 bg-primary/10 text-primary" : "border-destructive/25 bg-destructive/10 text-destructive",
      )}
    >
      {feedback.kind === "success" ? <CheckCircle2 size={16} className="shrink-0" /> : <TriangleAlert size={16} className="shrink-0" />}
      <span>{feedback.message}</span>
    </div>
  );
}

function ConversionTips(): JSX.Element {
  return (
    <Card variant="ghost" className="border-border/60 bg-[hsl(var(--surface-raised)/0.3)]">
      <CardContent className="space-y-2.5 p-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5 font-semibold text-foreground">
          <Sparkles size={14} className="text-[hsl(var(--gold))]" />
          Dicas de Alta Conversão LucraLink
        </div>
        <ul className="list-inside list-disc space-y-1.5">
          <li>
            <strong className="text-foreground">Preço tachado:</strong> Posts com ~De/Por~ convertem até 2x mais.
          </li>
          <li>
            <strong className="text-foreground">Imagens de produto:</strong> Posts visuais retêm 65% mais cliques nos grupos.
          </li>
          <li>
            <strong className="text-foreground">Proteção Anti-Ban:</strong> O LucraLink formata links diretos e canônicos dos marketplaces, prevenindo bloqueio do Telegram e WhatsApp.
          </li>
        </ul>
      </CardContent>
    </Card>
  );
}

interface ComposerState {
  bodyText: string;
  imageUrl: string;
  linkId: string;
  selected: Record<string, DestinationOption>;
  mode: PublishMode;
  scheduleDate: string;
  scheduleTime: string;
  draft: RecurrenceDraft;
}

const EMPTY: Omit<ComposerState, "draft"> = {
  bodyText: "",
  imageUrl: "",
  linkId: "",
  selected: {},
  mode: "now",
  scheduleDate: "",
  scheduleTime: "",
};

interface TextCardProps {
  readonly bodyText: string;
  readonly onBodyChange: (text: string) => void;
  readonly link: BroadcastLinkData | null;
  readonly onInsert: (text: string) => void;
  readonly canGenerate: boolean;
  readonly isGenerating: boolean;
  readonly onGenerate: () => void;
}

function TextCard({ bodyText, onBodyChange, link, onInsert, canGenerate, isGenerating, onGenerate }: TextCardProps): JSX.Element {
  const bodyTooLong = bodyText.length > MAX_CHARACTERS;
  return (
    <Card variant="raised">
      <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
        <CardTitle>Texto do post</CardTitle>
        <CardDescription>Escreva a mensagem ou insira um texto de disparo pronto ou salvo.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-5">
        <div className="space-y-3 border-b border-border/60 pb-2">
          <BroadcastTextPicker link={link} onInsert={onInsert} />
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="text-xs text-muted-foreground">Quer um texto personalizado para este produto?</span>
            <Button type="button" variant="gold" size="sm" onClick={onGenerate} disabled={isGenerating || !canGenerate} className="h-8 gap-1.5 text-xs shadow-xs">
              {isGenerating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              {isGenerating ? "Gerando copy..." : "Gerar com IA"}
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="post-text" variant="field">Conteúdo</Label>
          <textarea
            id="post-text"
            value={bodyText}
            onChange={(event) => onBodyChange(event.target.value)}
            placeholder="Escreva sua legenda, descrição ou promoção..."
            rows={7}
            maxLength={MAX_CHARACTERS + 100}
            className={cn(
              "flex w-full resize-y rounded-md border bg-[hsl(var(--input))] px-3 py-2 font-sans text-sm text-foreground placeholder:text-muted-foreground/60",
              "transition-colors duration-[120ms] hover:border-[hsl(var(--border-strong))]",
              "focus-visible:border-[hsl(var(--gold)/0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">Dica: selecione o link de afiliado abaixo para enriquecer o disparo.</span>
          <span className={cn("shrink-0 text-xs tabular-nums", bodyTooLong ? "font-semibold text-destructive" : "text-muted-foreground")}>
            {bodyText.length}/{MAX_CHARACTERS}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

interface MediaCardProps {
  readonly state: ComposerState;
  readonly patch: (next: Partial<ComposerState>) => void;
  readonly links: readonly AffiliateLink[];
  readonly selectedLink: AffiliateLink | undefined;
}

function MediaCard({ state, patch, links, selectedLink }: MediaCardProps): JSX.Element {
  return (
    <Card variant="raised">
      <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
        <CardTitle>Imagem e link de afiliado</CardTitle>
        <CardDescription>Ambos são opcionais, mas recomendados para maximizar cliques.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-5">
        <div className="space-y-2">
          <Label htmlFor="post-image" variant="field">URL da imagem (opcional)</Label>
          <div className="relative">
            <ImageIcon size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input id="post-image" type="url" value={state.imageUrl} onChange={(event) => patch({ imageUrl: event.target.value })} placeholder="https://exemplo.com/foto-do-produto.jpg" className="pl-9" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="post-link" variant="field">Link de afiliado (opcional)</Label>
          <select
            id="post-link"
            value={state.linkId}
            onChange={(event) => patch({ linkId: event.target.value })}
            className="h-9 w-full rounded-md border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))] px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Sem link</option>
            {links.map((link) => (
              <option key={link.id} value={link.id}>
                {link.marketplace_code} - {link.title ?? link.destination_url}
              </option>
            ))}
          </select>
          {selectedLink && (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-[hsl(var(--surface-raised)/0.35)] px-3 py-2 text-xs text-muted-foreground">
              <ExternalLink size={12} className="shrink-0" />
              <span className="truncate">{selectedLink.destination_url}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function modeIsReady(state: ComposerState): boolean {
  if (state.mode === "schedule") return scheduleToIso(state.scheduleDate, state.scheduleTime) !== null;
  if (state.mode === "recurring") return draftToRule(state.draft) !== null && draftStart(state.draft) !== null;
  return true;
}

function useComposer() {
  const [state, setState] = useState<ComposerState>(() => ({ ...EMPTY, draft: defaultDraft() }));
  const patch = (next: Partial<ComposerState>): void => setState((prev) => ({ ...prev, ...next }));
  const reset = (): void => setState({ ...EMPTY, draft: defaultDraft() });
  return { state, patch, reset };
}

export function CriarPostsPage(): JSX.Element {
  const { data: tenantBilling } = useTenant();
  const entitlements = useEntitlements(tenantBilling?.tenant);
  const { data: links = [] } = useLinks();
  const createScheduledPost = useCreateScheduledPost();
  const saveRecurring = useSaveRecurringPost();
  const { generateCopy, loading: isGeneratingCopy, error: aiCopyError } = useGenerateCopy();
  const { state, patch, reset } = useComposer();
  const [feedback, setFeedback] = useState<Feedback>(null);

  const hasScheduledPosts = entitlements.hasScheduledPosts;
  const selectedLink = useMemo(() => links.find((link) => link.id === state.linkId), [links, state.linkId]);
  const bodyTooLong = state.bodyText.length > MAX_CHARACTERS;
  const isPending = createScheduledPost.isPending || saveRecurring.isPending;
  const canSubmit =
    hasScheduledPosts &&
    state.bodyText.trim().length > 0 &&
    !bodyTooLong &&
    Object.keys(state.selected).length > 0 &&
    modeIsReady(state) &&
    !isPending;

  function insertText(text: string): void {
    patch({ bodyText: state.bodyText.trim().length > 0 ? `${state.bodyText.trimEnd()}\n\n${text}` : text });
  }

  async function handleGenerateAiCopy(): Promise<void> {
    if (!selectedLink?.destination_url) {
      setFeedback({ kind: "error", message: "Selecione um link de afiliado abaixo antes de gerar copy com inteligência artificial." });
      return;
    }
    try {
      const result = await generateCopy({
        productUrl: selectedLink.destination_url,
        affiliateUrl: selectedLink.destination_url,
        marketplaceLabel: selectedLink.marketplace_code,
      });
      if (result) {
        patch({ bodyText: result.mensagemCompleta });
        setFeedback({ kind: "success", message: "Copy gerada com inteligência artificial com sucesso!" });
      } else if (aiCopyError) {
        setFeedback({ kind: "error", message: aiCopyError });
      }
    } catch (err) {
      setFeedback({ kind: "error", message: err instanceof Error ? err.message : "Falha ao gerar copy com inteligência artificial." });
    }
  }

  async function submitRecurring(): Promise<void> {
    const rule = draftToRule(state.draft);
    const startsAt = draftStart(state.draft);
    if (!rule || !startsAt) return;
    await saveRecurring.mutateAsync({
      id: null,
      bodyText: state.bodyText.trim(),
      imageUrl: state.imageUrl.trim() || null,
      linkId: state.linkId || null,
      deliveries: toDeliveries(state.selected),
      rule,
      startsAt,
      endsAt: draftEnd(state.draft),
    });
  }

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return;
    try {
      if (state.mode === "recurring") {
        await submitRecurring();
      } else {
        await createScheduledPost.mutateAsync({
          bodyText: state.bodyText.trim(),
          imageUrl: state.imageUrl.trim() || null,
          linkId: state.linkId || null,
          scheduledAt: state.mode === "schedule" ? scheduleToIso(state.scheduleDate, state.scheduleTime) : null,
          deliveries: toDeliveries(state.selected),
        });
      }
      setFeedback({ kind: "success", message: successMessage(state.mode) });
      reset();
    } catch (error) {
      setFeedback({ kind: "error", message: error instanceof Error ? error.message : "Não foi possível criar o post." });
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Criar Posts"
        subtitle="Envie um post para seus grupos Telegram e chats WhatsApp conectados: agora, agendado ou recorrente."
      />
      {!hasScheduledPosts && <ProBanner />}
      {feedback && <FeedbackBanner feedback={feedback} />}

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-7">
          <fieldset disabled={!hasScheduledPosts} className="space-y-6">
            <TextCard
              bodyText={state.bodyText}
              onBodyChange={(bodyText) => patch({ bodyText })}
              link={linkData(selectedLink)}
              onInsert={insertText}
              canGenerate={Boolean(state.linkId)}
              isGenerating={isGeneratingCopy}
              onGenerate={() => void handleGenerateAiCopy()}
            />
            <MediaCard state={state} patch={patch} links={links} selectedLink={selectedLink} />
            <Card variant="raised">
              <CardHeader className="border-b border-border bg-[hsl(var(--surface-raised)/0.45)]">
                <CardTitle>Destinos</CardTitle>
                <CardDescription>Selecione os grupos Telegram e chats WhatsApp conectados. Até 10 destinos por post.</CardDescription>
              </CardHeader>
              <CardContent className="pt-5">
                <DestinationPicker selected={state.selected} onChange={(selected) => patch({ selected })} />
              </CardContent>
            </Card>

            <PublishModeCard
              mode={state.mode}
              onModeChange={(mode) => patch({ mode })}
              scheduleDate={state.scheduleDate}
              scheduleTime={state.scheduleTime}
              onScheduleChange={(scheduleDate, scheduleTime) => patch({ scheduleDate, scheduleTime })}
              draft={state.draft}
              onDraftChange={(draft) => patch({ draft })}
              canSubmit={canSubmit}
              isPending={isPending}
              onSubmit={() => void handleSubmit()}
            />
          </fieldset>
        </div>

        <div className="space-y-5 lg:sticky lg:top-6 lg:col-span-5">
          <PostPreviewSimulator bodyText={state.bodyText} imageUrl={state.imageUrl} affiliateUrl={selectedLink?.destination_url} />
          <ConversionTips />
        </div>
      </div>
    </div>
  );
}
